import { useAuthStore } from '@/stores/auth.store';
import { getActiveTenantId } from '@/lib/activeTenant';
import { messagingApi } from '@/lib/messaging/api';

/**
 * Shared mic constraints for every outbound call — mirrors the legacy app so
 * calls don't go out with raw `audio: true` (no echo-cancellation / noise-
 * suppression / gain-control), which produced uneven audio for the remote party.
 */
export const CALL_AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
} as const;

/** Minimal shape of a WebRTC codec capability — enough to order Opus first
 *  without depending on `RTCRtpCodecCapability` being in the TS DOM lib. */
type CodecCapability = { mimeType?: string };

/**
 * Prefer Opus for the WebRTC leg. Without it the call can negotiate a narrowband
 * codec (PCMU/PCMA @ 8 kHz) — the usual cause of "robotic" audio the remote party
 * hears. Telnyx takes codec ordering via `preferred_codecs` on newCall. Returns
 * undefined when the browser can't report capabilities (Telnyx keeps its default).
 */
export function getPreferredAudioCodecs(): CodecCapability[] | undefined {
  try {
    const receiver = (
      globalThis as {
        RTCRtpReceiver?: { getCapabilities?: (k: string) => { codecs?: CodecCapability[] } | null };
      }
    ).RTCRtpReceiver;
    if (!receiver || typeof receiver.getCapabilities !== 'function') {
      return undefined;
    }
    const codecs = receiver.getCapabilities('audio')?.codecs ?? [];
    if (!codecs.length) return undefined;
    const isOpus = (c: CodecCapability) => /opus/i.test(c.mimeType || '');
    const isDtmf = (c: CodecCapability) => /telephone-event/i.test(c.mimeType || '');
    const opus = codecs.filter(isOpus);
    if (!opus.length) return undefined;
    const dtmf = codecs.filter(isDtmf);
    const rest = codecs.filter((c) => !isOpus(c) && !isDtmf(c));
    // Opus first; keep DTMF + the rest so PSTN/SIP destinations still connect.
    return [...opus, ...dtmf, ...rest];
  } catch {
    return undefined;
  }
}

/** The server's maximum page size for phone numbers (`@Max(200)` on the DTO). */
const CALLER_NUMBER_PAGE_SIZE = 200;

/** A bound on the paging loop: 20 pages is 4,000 lines, far past any tenant. */
const CALLER_NUMBER_PAGE_LIMIT = 20;

/**
 * Every line the tenant may place a call FROM, in the order the tenant holds
 * them.
 *
 * The FIRST is the default caller ID: Telnyx rejects an outbound PSTN call
 * with no `callerNumber` (the call dies at setup → "CALL DOES NOT EXIST"), so
 * the softphone must send one. The rest are what lets a call back present the
 * line the customer originally rang, falling back to the first when that line
 * is no longer one of ours (ported away, deactivated, or a call from before it
 * changed hands). Only real voice lines are included — SMS-only AUTOMATION
 * numbers aren't voice-provisioned, and Telnyx rejects a leg presenting one.
 * Empty for a tenant with no usable number, so callers can refuse the call
 * rather than dial out with a number Telnyx will reject.
 */
export async function fetchCallerNumbers(): Promise<string[]> {
  const lines: string[] = [];
  try {
    for (let page = 1; page <= CALLER_NUMBER_PAGE_LIMIT; page += 1) {
      const res = await messagingApi.phoneNumbers.list({
        status: 'ACTIVE',
        page,
        pageSize: CALLER_NUMBER_PAGE_SIZE,
      });
      const numbers = res?.data ?? [];
      for (const number of numbers) {
        if (number.e164 && number.purpose !== 'AUTOMATION') lines.push(number.e164);
      }
      // `hasMore` is the server's own answer; the length check only covers a
      // response that omits it. A tenant with more lines than one page had its
      // later lines missing from this list entirely, so a call back on one of
      // them silently fell back to the default caller ID.
      const more = res?.hasMore ?? numbers.length === CALLER_NUMBER_PAGE_SIZE;
      if (!more || numbers.length === 0) break;
    }
  } catch {
    // Keep whatever pages did arrive: the first line is the default caller ID,
    // so a failure on page 2 costs the choice of line, not the ability to call.
  }
  return lines;
}

/**
 * The external Telnyx calling service (rtc.getnotifi.com) — separate host from
 * operations. The browser fetches SIP credentials from it (authenticated with
 * the notifi-2.0 ES256 JWT + x-tenant-id), then registers a Telnyx WebRTC
 * client with those credentials so Telnyx can ring this tab directly.
 *
 * Configured per env via VITE_CALLING_SERVICE_URL. Falls back to the production
 * RTC host (rtc.getnotifi.com) only when the var is UNSET (undefined), so
 * calling works without extra config. An explicit empty value stays empty — the
 * kill switch that keeps calling inert (never posting the user's token to a
 * host) for a build that deliberately disables it. Uses `??`, not `||`, so ''
 * is not treated as unset. Override the var per environment to point elsewhere.
 */
const CALLING_SERVICE_URL = (
  (import.meta.env.VITE_CALLING_SERVICE_URL as string | undefined) ?? 'https://rtc.getnotifi.com'
).replace(/\/+$/, '');

/**
 * Whether a calling service base URL is resolved for this build. True in the
 * common case (unset ⇒ prod fallback, or an explicit host), but an explicit
 * empty `VITE_CALLING_SERVICE_URL` leaves it false so callers skip registration
 * — the kill switch. Callers must branch on this before registering.
 */
export const isCallingConfigured = CALLING_SERVICE_URL !== '';

/**
 * `fetch` against the calling service, with the one failure it has that `fetch`
 * describes uselessly turned into something diagnosable.
 *
 * A cross-origin request the browser refuses — the calling service answering a
 * preflight, or an error, without `Access-Control-Allow-Origin` — rejects with
 * a bare `TypeError: Failed to fetch` that names neither the host nor the path.
 * NOT-747 was exactly that, and the message reached both a user-facing toast
 * and Sentry with nothing in it to act on. Naming the host and route here costs
 * nothing and makes the next occurrence self-describing.
 */
async function callingServiceFetch(path: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(`${CALLING_SERVICE_URL}${path}`, init);
  } catch (cause) {
    throw new Error(
      `Calling service unreachable: ${init.method ?? 'GET'} ${CALLING_SERVICE_URL}${path} ` +
        `(${cause instanceof Error ? cause.message : String(cause)})`,
      { cause },
    );
  }
}

const SIP_CREDENTIALS_PATH = '/api/devices/sip/credentials';
const DEVICE_ID_KEY = 'voip_device_id';

export interface SipCredentials {
  sipId: string;
  sipSecret: string;
  realm?: string;
  wsServers?: string;
}

/** Stable per-browser device id so the calling service can vend a reusable
 *  web SIP credential per (tenant, user, device). */
export function getOrCreateDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    // Private mode / storage disabled — fall back to an ephemeral id.
    return `web-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function getDeviceName(): string {
  // Keep this short and single-line: the calling service folds it into the
  // Telnyx credential connection name, which Telnyx caps at 64 chars. A long
  // "Browser (…)" string plus the user id can overflow, so send a bare "Web".
  return 'Web';
}

/**
 * POST {CALLING_SERVICE_URL}/api/devices/sip/credentials with the notifi JWT +
 * x-tenant-id. Returns the SIP login/secret the Telnyx WebRTC client registers
 * with. Throws on auth/permission failure (e.g. 403 CALL_PERMISSIONS_DISABLED).
 */
export async function fetchSipCredentials(): Promise<SipCredentials> {
  const token = useAuthStore.getState().token;
  const tenantId = getActiveTenantId();
  if (!token) {
    throw new Error('Not authenticated — no token for SIP credentials');
  }

  const response = await callingServiceFetch(SIP_CREDENTIALS_PATH, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
    },
    body: JSON.stringify({
      platform: 'web',
      deviceId: getOrCreateDeviceId(),
      deviceName: getDeviceName(),
    }),
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`SIP credential request failed: ${response.status} ${detail.slice(0, 200)}`);
  }

  // The calling service wraps the payload as { success, data: {...} } or returns
  // the object directly — accept both.
  const json = (await response.json()) as
    | { data?: Partial<SipCredentials> }
    | Partial<SipCredentials>;
  const data = ('data' in json && json.data ? json.data : json) as Partial<SipCredentials>;

  if (!data.sipId || !data.sipSecret) {
    throw new Error('SIP credential response missing sipId/sipSecret');
  }
  return {
    sipId: data.sipId,
    sipSecret: data.sipSecret,
    realm: data.realm,
    wsServers: data.wsServers,
  };
}

const DO_NOT_DISTURB_PATH = '/api/devices/sip/do-not-disturb';

/**
 * The signed-in user's Do Not Disturb, stored on the calling service because
 * that is where the ringing is decided. While it is on, inbound calls skip every
 * device this person owns — this tab and the mobile app alike — so the setting
 * lives with the user, not with the browser.
 */
export interface DoNotDisturbDevice {
  deviceId: string;
  deviceName: string | null;
  /** 'web' for the browser credential a person's logins share; 'ios' / 'android' for a phone. */
  platform: string | null;
  lastSeen: string | null;
  enabled: boolean;
  /** ISO time this device starts ringing again; null for "until I turn it off". */
  until: string | null;
}

/**
 * The signed-in user's devices and which of them Do Not Disturb has silenced.
 *
 * Stored on the calling service because that is where ringing is decided:
 * silencing a device takes its SIP address out of the dial, which is what stops
 * the mobile app's push as well as this tab.
 */
async function doNotDisturbRequest(init: RequestInit = {}): Promise<unknown> {
  const token = useAuthStore.getState().token;
  const tenantId = getActiveTenantId();
  if (!token) {
    throw new Error('Not authenticated — no token for Do Not Disturb');
  }

  const response = await callingServiceFetch(DO_NOT_DISTURB_PATH, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
    },
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Do Not Disturb request failed: ${response.status} ${detail.slice(0, 200)}`);
  }

  // Wrapped as { success, data: {...} } or returned directly — accept both, the
  // same as fetchSipCredentials, so a raw payload is never misread.
  const json = (await response.json()) as { data?: unknown };
  return json && typeof json === 'object' && 'data' in json && json.data ? json.data : json;
}

function asDevice(raw: unknown): DoNotDisturbDevice | null {
  const d = raw as Partial<DoNotDisturbDevice> | null;
  if (!d || typeof d.deviceId !== 'string') return null;
  const enabled = d.enabled === true;
  return {
    deviceId: d.deviceId,
    deviceName: typeof d.deviceName === 'string' ? d.deviceName : null,
    platform: typeof d.platform === 'string' ? d.platform : null,
    lastSeen: typeof d.lastSeen === 'string' ? d.lastSeen : null,
    enabled,
    until: enabled && typeof d.until === 'string' ? d.until : null,
  };
}

export async function fetchDoNotDisturb(): Promise<DoNotDisturbDevice[]> {
  const data = (await doNotDisturbRequest()) as { devices?: unknown };
  const devices = Array.isArray(data?.devices) ? data.devices : [];
  return devices.map(asDevice).filter((d): d is DoNotDisturbDevice => d !== null);
}

/**
 * Silence one device until a time (or until turned off), or let it ring again.
 * The end time is ignored when turning off.
 */
export async function saveDoNotDisturb(
  deviceId: string,
  enabled: boolean,
  until: Date | null = null,
): Promise<DoNotDisturbDevice | null> {
  const body = enabled
    ? { deviceId, enabled, until: until ? until.toISOString() : null }
    : { deviceId, enabled };
  return asDevice(await doNotDisturbRequest({ method: 'PUT', body: JSON.stringify(body) }));
}
